import { useState, useEffect, useRef, useCallback } from 'react';
import { requestSilentToken } from '../components/Auth/googleClient';
import { registerUnauthorizedHandler } from '../services/api';

const STORAGE_KEY = 'belodbot-user';
// Refresh silently this many ms before the token actually expires.
// Also used as a safety buffer when deciding if a stored token is still usable
// on bootstrap, so we never initialize with a token that's about to expire.
const REFRESH_SKEW_MS = 5 * 60 * 1000; // 5 minutes

// A token is considered still valid only if it lives past the buffer window,
// i.e. Date.now() < (exp * 1000) - buffer.
const isTokenValid = (expSeconds) =>
  !!expSeconds && Date.now() < expSeconds * 1000 - REFRESH_SKEW_MS;

const decodeJWT = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return JSON.parse(atob(base64));
  }
};

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const refreshTimer = useRef(null);

  const applyCredential = useCallback((credential) => {
    const payload = decodeJWT(credential);
    const userData = {
      name: payload.name,
      email: payload.email,
      picture: payload.picture,
      credential,
      tokenExp: payload.exp,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
    setUser(userData);
    setIsSignedIn(true);
    scheduleSilentRefresh(payload.exp);
    return userData;
  }, []); // eslint-disable-line

  // Fired by Google (button click, One Tap, or silent auto-select)
  const handleSignIn = useCallback((response) => {
    if (!response || !response.credential) {
      console.error('Invalid sign-in response:', response);
      return;
    }
    applyCredential(response.credential);
    console.info('User signed in / token refreshed');
  }, [applyCredential]);

  // Schedule a silent renewal shortly before the current token expires
  const scheduleSilentRefresh = useCallback((expSeconds) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    if (!expSeconds) return;
    const msUntilRefresh = expSeconds * 1000 - Date.now() - REFRESH_SKEW_MS;
    const delay = Math.max(msUntilRefresh, 1000);
    refreshTimer.current = setTimeout(() => {
      console.info('Attempting silent token refresh...');
      requestSilentToken(handleSignIn).catch((e) => console.warn('Silent refresh failed', e));
    }, delay);
  }, [handleSignIn]);

  // On mount: restore session, or try a silent sign-in if the token expired
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const userData = JSON.parse(saved);
        if (isTokenValid(userData.tokenExp)) {
          setUser(userData);
          setIsSignedIn(true);
          scheduleSilentRefresh(userData.tokenExp);
          return;
        }
        // Token expired or within the buffer window — try to renew silently
        // before showing the login screen.
        console.info('Stored token expired/expiring; attempting silent renewal');
        requestSilentToken(handleSignIn).catch(() => {
          localStorage.removeItem(STORAGE_KEY);
        });
        return;
      } catch (error) {
        console.error('Error loading user from localStorage:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    // No stored session — attempt one-tap silent sign-in (harmless if it does nothing)
    requestSilentToken(handleSignIn).catch(() => {});
  }, [handleSignIn, scheduleSilentRefresh]);

  useEffect(() => () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); }, []);

  const handleSignOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setIsSignedIn(false);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    if (window.google && window.google.accounts) {
      window.google.accounts.id.disableAutoSelect();
    }
    console.info('User signed out');
  }, []);

  // Invoked by the API layer on a 401 from the gateway: the stored token is no
  // longer accepted. Clear the cached session and re-trigger GIS (silent prompt),
  // which will auto-select when an active Google session exists, or fall back to
  // the login screen. We do NOT disableAutoSelect here — unlike explicit sign-out,
  // we want auto-login to recover the session seamlessly.
  const handleUnauthorized = useCallback(() => {
    console.warn('Received 401 — clearing session and re-authenticating');
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setIsSignedIn(false);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    requestSilentToken(handleSignIn).catch((e) => console.warn('Re-auth prompt failed', e));
  }, [handleSignIn]);

  // Register the 401 handler with the API layer once.
  useEffect(() => {
    registerUnauthorizedHandler(handleUnauthorized);
    return () => registerUnauthorizedHandler(null);
  }, [handleUnauthorized]);

  return { user, isSignedIn, handleSignIn, handleSignOut };
};
